import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  LinearProgress,
  Snackbar,
  Alert,
  Menu,
  MenuItem,
  FormControl,
  InputLabel,
} from '@mui/material';
import Select from '@mui/material/Select';
import { Edit as EditIcon, RecentActorsOutlined as UserIcon, DeleteOutlined as DeleteIcon } from '@mui/icons-material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { differenceInDays, differenceInHours, differenceInMinutes, differenceInSeconds, format, addYears, addMonths } from 'date-fns';
import api from "../../services/api";
import { API_ENDPOINTS } from "../../config/api";
import { DataGrid } from '@mui/x-data-grid';

export default function ClientManagement() {
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [openDialog, setOpenDialog] = useState(false);
  const [openDialogUser, setOpenDialogUser] = useState(false);
  const [selectedClient, setSelectedClient] = useState(null);
  const [editFormData, setEditFormData] = useState({
    client_name: '',
    parent_id: '',
    name: '',
    email: '',
    expiresAt: null
  });
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('success');
  const [timeLeft, setTimeLeft] = useState({});
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);
  const [openDatePicker, setOpenDatePicker] = useState(false);
  const [users, setUsers] = useState([]);
  const [isLoadingUsers, setIsLoadingUsers] = useState(false);
  const [totalItems, setTotalItems] = useState(0);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(5);

  useEffect(() => {
    fetchClients();
  }, []);

  const updateTimeLeft = useCallback(() => {
    if (!Array.isArray(clients) || clients.length === 0) {
      return;
    }

    const newTimeLeft = {};
    clients.forEach(client => {
      if (!client || !client.expiresAt) {
        newTimeLeft[client.id] = {
          notSet: true
        };
        return;
      }

      const expiryDate = new Date(client.expiresAt);
      const now = new Date();
      
      if (expiryDate > now) {
        const days = differenceInDays(expiryDate, now);
        const hours = differenceInHours(expiryDate, now) % 24;
        const minutes = differenceInMinutes(expiryDate, now) % 60;
        const seconds = differenceInSeconds(expiryDate, now) % 60;
        
        newTimeLeft[client.id] = {
          days,
          hours,
          minutes,
          seconds,
          expired: false,
          notSet: false,
          warning: days < 30
        };
      } else {
        newTimeLeft[client.id] = {
          days: 0,
          hours: 0,
          minutes: 0,
          seconds: 0,
          expired: true,
          notSet: false,
          warning: false
        };
      }
    });
    setTimeLeft(newTimeLeft);
    setLoading(false);
  }, [clients]);

  useEffect(() => {
    const timer = setInterval(() => {
      updateTimeLeft();
    }, 1000);

    return () => clearInterval(timer);
  }, [updateTimeLeft]);

  const fetchClients = async () => {
    setLoading(true);
    try {
      const response = await api.get(API_ENDPOINTS.CLIENT.GET_ALL);
      const clientsData = response.data?.clients || [];
      
      const formattedClients = clientsData.map(client => ({
        ...client,
        expiresAt: client.expiresAt ? new Date(client.expiresAt).toISOString() : null
      }));
      
      setClients(formattedClients);
    } catch (err) {
      console.error('Error fetching clients:', err);
      setError(err.response?.data?.message || 'Failed to fetch clients');
      setLoading(false);
      setClients([]);
    }
  };

  const fetchUsers = useCallback(async (clientId) => {
    setIsLoadingUsers(true);
    setUsers([]);
    try {
      const response = await api.get(API_ENDPOINTS.USER.GET_ALL+"?clientId="+clientId+"", {
        params: { timestamp: new Date().getTime(), page: page + 1, limit: pageSize }
      });
      setUsers(response.data.users);
      setTotalItems(response.data.totalItems);
    } catch (err) {
      console.error('Error fetching users:', err);
    }
    setIsLoadingUsers(false);
  }, [page, pageSize]);

  const columns = [
    { field: 'name', headerName: 'Name', flex: 0.2, minWidth: 150 },
    { field: 'email', headerName: 'Email', flex: 0.25, minWidth: 200 },
  ];

  const handleEditClick = (client) => {
    setSelectedClient(client);
    setEditFormData({
      client_name: client.client_name,
      parent_id: client.parent_id || '',
      name: client.name,
      email: client.email,
      expiresAt: new Date(client.expiresAt)
    });
    setOpenDialog(true);
  };

  const handleShowUserClick = (client) => {
    setSelectedClient(client);
    fetchUsers(client.id);
    setOpenDialogUser(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm("Are you sure you want to delete this client?")) {
      try {
        setLoading(true);
        await api.delete(API_ENDPOINTS.CLIENT.DELETE(id))
        await fetchClients();
        setSnackbarMessage('Client deleted successfully!');
        setSnackbarSeverity('success');
        setOpenSnackbar(true);
      } catch (err) {
        console.error('Error deleting client:', err);
        setSnackbarMessage(err.response?.data?.message || 'Failed to delete client');
        setSnackbarSeverity('error');
        setOpenSnackbar(true);
        setLoading(false);
      }
    }
  }

  const handleClose = () => {
    setOpenDialog(false);
    setOpenDialogUser(false);
    setSelectedClient(null);
    setEditFormData({
      client_name: '',
      parent_id: '',
      name: '',
      email: '',
      expiresAt: null
    });
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setEditFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const requestData = {
        ...editFormData,
        expiresAt: editFormData.expiresAt.toISOString()
      };

      const response = await api.put(
        API_ENDPOINTS.CLIENT.UPDATE(selectedClient.id),
        requestData
      );

      if (response.status === 200 || response.status === 202) {
        handleClose();
        await fetchClients();
        setSnackbarMessage('Client updated successfully!');
        setSnackbarSeverity('success');
        setOpenSnackbar(true);
      }
    } catch (err) {
      console.error('Error updating client:', err);
      setSnackbarMessage(err.response?.data?.message || 'Failed to update client');
      setSnackbarSeverity('error');
      setOpenSnackbar(true);
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) {
        return 'Invalid Date';
      }
      return format(date, 'dd MMMM yyyy');
    } catch (err) {
      console.error('Error formatting date:', err);
      return 'Invalid Date';
    }
  };

  const handleDurationClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleDurationClose = () => {
    setAnchorEl(null);
  };

  const handleDurationSelect = (duration) => {
    let newDate;
    switch (duration) {
      case '1month':
        newDate = addMonths(new Date(), 1);
        break;
      case '3months':
        newDate = addMonths(new Date(), 3);
        break;
      case '6months':
        newDate = addMonths(new Date(), 6);
        break;
      case '1year':
        newDate = addYears(new Date(), 1);
        break;
      case '2years':
        newDate = addYears(new Date(), 2);
        break;
      default:
        newDate = addYears(new Date(), 1);
    }
    setEditFormData(prev => ({
      ...prev,
      expiresAt: newDate
    }));
    handleDurationClose();
  };

  if (error) return <Typography color="error">{error}</Typography>;

  return (
    <Box sx={{ width: '100%', pl: 0.2, pr: 2 }}>
      <Typography variant="h4" sx={{ mb: 3, fontWeight: 'bold' }}>
        Client Management
      </Typography>

      <TableContainer component={Paper} sx={{ borderRadius: 2, boxShadow: 3 }}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Client Name</TableCell>
              <TableCell>Admin Email</TableCell>
              <TableCell>Subscription Expiry</TableCell>
              <TableCell>Time Remaining</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={5} sx={{ textAlign: 'center', p: 0 }}>
                  <LinearProgress sx={{ color: 'primary.main' }} />
                  {/* <CircularProgress sx={{ color: 'primary.main' }} /> */}
                </TableCell>
              </TableRow>
            ) : (
              clients.map((client) => (
                <TableRow 
                  key={client.id}
                  sx={{
                    backgroundColor: timeLeft[client.id]?.expired 
                      ? 'rgba(227, 27, 84, 0.08)' 
                      : timeLeft[client.id]?.warning 
                      ? 'rgba(255, 152, 0, 0.08)'
                      : 'inherit'
                  }}
                >
                  <TableCell>{client.name}</TableCell>
                  <TableCell>{client.email}</TableCell>
                  <TableCell>{client.expiresAt ? formatDate(client.expiresAt) : 'No date set'}</TableCell>
                  <TableCell>
                    {timeLeft[client.id]?.notSet ? (
                      'Date not set'
                    ) : timeLeft[client.id]?.expired ? (
                      <Typography color="error">Expired</Typography>
                    ) : timeLeft[client.id]?.warning ? (
                      <Typography color="warning.main">
                        {`${timeLeft[client.id]?.days}d ${timeLeft[client.id]?.hours}h ${timeLeft[client.id]?.minutes}m ${timeLeft[client.id]?.seconds}s`}
                      </Typography>
                    ) : (
                      `${timeLeft[client.id]?.days}d ${timeLeft[client.id]?.hours}h ${timeLeft[client.id]?.minutes}m ${timeLeft[client.id]?.seconds}s`
                    )}
                  </TableCell>
                  <TableCell>
                    <IconButton 
                      onClick={() => handleShowUserClick(client)}
                      sx={{ 
                        color: '#E31B54',
                        '&:hover': { 
                          backgroundColor: 'rgba(227, 27, 84, 0.08)'
                        }
                      }}
                    >
                      <UserIcon />
                    </IconButton>
                    <IconButton 
                      onClick={() => handleEditClick(client)}
                      sx={{ 
                        color: '#E31B54',
                        '&:hover': { 
                          backgroundColor: 'rgba(227, 27, 84, 0.08)'
                        }
                      }}
                    >
                      <EditIcon />
                    </IconButton>
                    <IconButton 
                      sx={{ color: "#E31B54" }} 
                      onClick={() => handleDelete(client.id)}
                    >
                      <DeleteIcon fontSize="inherit" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={openDialog} onClose={handleClose} maxWidth="sm" fullWidth>
        <DialogTitle>Edit Client</DialogTitle>
        <DialogContent>
          <Box component="form" sx={{ mt: 2 }}>
            <TextField
              fullWidth
              label="Client Name"
              name="name"
              value={editFormData.name}
              onChange={handleChange}
              sx={{ mb: 2 }}
            />
            <TextField
              disabled
              fullWidth
              label="Admin Email"
              name="email"
              type="email"
              value={editFormData.email}
              onChange={handleChange}
              sx={{ mb: 2 }}
            />
            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel id="parent-select-label">Client Parent</InputLabel>
              <Select
                fullWidth
                labelId="parent-select-label"
                label="Client Parent"
                name="parent_id"
                value={editFormData.parent_id || ''}
                onChange={handleChange}
                sx={{ mb: 2 }}
              >
                <MenuItem value="">No Parent</MenuItem>
                {clients.filter(client => client?.id !== selectedClient?.id).map((client) => (
                  <MenuItem key={client.id} value={client.id}>
                    {client.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <DatePicker
                  label="Subscription Expiry"
                  value={editFormData.expiresAt}
                  onChange={(newValue) => {
                    setEditFormData(prev => ({
                      ...prev,
                      expiresAt: newValue
                    }));
                  }}
                  format="dd MMMM yyyy"
                  open={openDatePicker}
                  onOpen={() => setOpenDatePicker(true)}
                  onClose={() => setOpenDatePicker(false)}
                  onClick={() => setOpenDatePicker(true)}
                  slotProps={{
                    textField: {
                      onClick: () => setOpenDatePicker(true),
                      sx: { flex: 1 }
                    }
                  }}
                />
              </LocalizationProvider>
              <Button
                onClick={handleDurationClick}
                variant="outlined"
                sx={{
                  color: '#E31B54',
                  borderColor: '#E31B54',
                  textTransform: 'none',
                  borderRadius: 2,
                  '&:hover': {
                    borderColor: '#C41747',
                    bgcolor: 'rgba(227, 27, 84, 0.04)',
                  },
                }}
              >
                <Typography sx={{ 
                  display: { xs: 'block', md: 'none' }  // Show "Preset" on mobile
                }}>
                  Preset
                </Typography>
                <Typography sx={{ 
                  display: { xs: 'none', md: 'block' }  // Show "Preset Durations" on desktop
                }}>
                  Preset Durations
                </Typography>
              </Button>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>Cancel</Button>
          <Button onClick={handleSubmit} variant="contained" sx={{ bgcolor: '#E31B54' }}>
            Save Changes
          </Button>
        </DialogActions>

        <Menu
          anchorEl={anchorEl}
          open={open}
          onClose={handleDurationClose}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'right',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'right',
          }}
          PaperProps={{
            elevation: 3,
            sx: {
              mt: 1,
              minWidth: 180,
              borderRadius: 2,
              backgroundColor: 'background.paper',
              '& .MuiMenuItem-root': {
                px: 2,
                py: 1.5,
                borderRadius: 1,
                margin: '4px 8px',
                typography: 'body2',
                '&:hover': {
                  backgroundColor: 'rgba(227, 27, 84, 0.08)',
                  color: '#E31B54',
                },
                '&.Mui-selected': {
                  backgroundColor: 'rgba(227, 27, 84, 0.12)',
                  color: '#E31B54',
                  '&:hover': {
                    backgroundColor: 'rgba(227, 27, 84, 0.16)',
                  },
                },
              },
            },
          }}
        >
          <MenuItem onClick={() => handleDurationSelect('1month')}>
            <Typography>1 Month</Typography>
          </MenuItem>
          <MenuItem onClick={() => handleDurationSelect('3months')}>
            <Typography>3 Months</Typography>
          </MenuItem>
          <MenuItem onClick={() => handleDurationSelect('6months')}>
            <Typography>6 Months</Typography>
          </MenuItem>
          <MenuItem onClick={() => handleDurationSelect('1year')}>
            <Typography>1 Year</Typography>
          </MenuItem>
          <MenuItem onClick={() => handleDurationSelect('2years')}>
            <Typography>2 Years</Typography>
          </MenuItem>
        </Menu>
      </Dialog>

      <Dialog open={openDialogUser} onClose={handleClose} maxWidth="md" fullWidth>
        <DialogTitle>Users of {selectedClient?.name}</DialogTitle>
        <DialogContent>
          <DataGrid
            rows={users.map((user) => ({ ...user, client_id: user.client_id?.id, client_name: user.client_id?.name }))}
            columns={columns}
            loading={isLoadingUsers}
            pagination
            paginationMode="server"
            rowCount={totalItems}
            pageSizeOptions={[5, 10, 25]}
            paginationModel={{ page, pageSize }}
            onPaginationModelChange={(newModel) => {
              setPage(newModel.page);
              setPageSize(newModel.pageSize);
            }}
            disableRowSelectionOnClick
            getRowId={(row) => row.id}
            disableColumnMenu
            sx={{
              borderRadius: 6,
              borderColor: 'background.primary',
              boxShadow: 1,
              color: 'text.secondary',
              bgcolor: 'background.paper',
              '& .MuiDataGrid-cell:focus': {
                outline: 'none',
              },
              '& .MuiDataGrid-columnHeader': {
                '&:focus': {
                  outline: 'none',
                },
                '&:focus-within': {
                  outline: 'none',
                }
              },
              '& .MuiDataGrid-columnSeparator': {
                display: 'none',
              },
              '& .MuiDataGrid-cell': {
                borderBottom: '1px solid',
                borderColor: 'background.default',
              },
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>Cancel</Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={openSnackbar}
        autoHideDuration={6000}
        onClose={() => setOpenSnackbar(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert 
          onClose={() => setOpenSnackbar(false)} 
          severity={snackbarSeverity}
          sx={{ width: '100%' }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
} 
